import React, { useState, useRef, useMemo, useEffect } from "react"
import moment from "moment"
import { Spinner } from "reactstrap"

import calendarIcon from "assets/images/icons/calendar.svg"
import { DATE_PERIODS_TYPES } from "constants/DatePeriods"

const WeekSelector = ({
   selectedWeeks = [],
   onChange,
   disabled = false,
   isLoading = false,
   selectedMonth,
   placeholder = "Select Weeks",
   productionLineId,
   startDate,
}) => {
   const [isOpen, setIsOpen] = useState(false)
   const wrapperRef = useRef(null)

   const generateWeekOptions = () => {
      const options = []
      if (!selectedMonth) return options

      const monthStart = moment(selectedMonth.startDate)
      const monthEnd = moment(selectedMonth.endDate)

      let current = monthStart.clone().startOf(DATE_PERIODS_TYPES.WEEK)
      let weekNumber = 1

      while (
         current.isBefore(monthEnd) ||
         current.isSame(monthStart, DATE_PERIODS_TYPES.MONTH)
      ) {
         const weekEnd = current.clone().endOf(DATE_PERIODS_TYPES.WEEK)

         if (
            current.isSameOrBefore(monthEnd) &&
            weekEnd.isSameOrAfter(monthStart)
         ) {
            options.push({
               value: current.toDate(),
               label: `Week ${weekNumber}: ${current.format(
                  "MMM DD"
               )} - ${weekEnd.format("MMM DD")}`,
               startDate: current.toDate(),
               endDate: weekEnd.toDate(),
               weekNumber: weekNumber,
            })
            weekNumber++
         }
         current.add(1, DATE_PERIODS_TYPES.WEEK)
      }
      return options
   }

   const isWeekSelected = option => {
      return selectedWeeks.some(selected =>
         moment(selected.startDate).isSame(
            moment(option.startDate),
            DATE_PERIODS_TYPES.DAY
         )
      )
   }

   const canSelectWeek = option => {
      if (startDate) {
         const optionStartDate = moment(option.startDate)
         const minStartDate = moment(startDate)
         if (optionStartDate.isBefore(minStartDate, DATE_PERIODS_TYPES.DAY)) {
            return false
         }
      }

      if (selectedWeeks.length === 0) {
         return true
      }

      const isSelected = isWeekSelected(option)

      if (isSelected) {
         const sortedWeeks = [...selectedWeeks].sort((a, b) =>
            moment(a.startDate).diff(moment(b.startDate))
         )
         const lastWeek = sortedWeeks[sortedWeeks.length - 1]
         return moment(lastWeek.startDate).isSame(
            moment(option.startDate),
            DATE_PERIODS_TYPES.DAY
         )
      }

      return true
   }

   const handleWeekSelect = option => {
      const isSelected = isWeekSelected(option)
      let newSelectedWeeks

      if (isSelected) {
         newSelectedWeeks = selectedWeeks.filter(
            week =>
               !moment(week.startDate).isSame(
                  moment(option.startDate),
                  DATE_PERIODS_TYPES.DAY
               )
         )
      } else {
         if (canSelectWeek(option)) {
            newSelectedWeeks = [...selectedWeeks, option].sort(
               (firstWeek, secondWeek) =>
                  firstWeek.weekNumber - secondWeek.weekNumber
            )
         } else {
            return
         }
      }

      onChange(newSelectedWeeks)
   }

   const handleInputClick = () => {
      if (!disabled) setIsOpen(!isOpen)
   }

   const handleIconClick = () => {
      if (!disabled) setIsOpen(!isOpen)
   }

   const handleDocumentClick = event => {
      if (
         isOpen &&
         wrapperRef.current &&
         !wrapperRef.current.contains(event.target)
      ) {
         setIsOpen(false)
      }
   }

   useEffect(() => {
      if (isOpen) {
         document.addEventListener("mousedown", handleDocumentClick)
      } else {
         document.removeEventListener("mousedown", handleDocumentClick)
      }
      return () =>
         document.removeEventListener("mousedown", handleDocumentClick)
   }, [isOpen])

   const options = useMemo(
      () => generateWeekOptions(),
      [selectedMonth, productionLineId]
   )

   const formatDisplayValue = () => {
      if (selectedWeeks.length === 0) return placeholder
      if (selectedWeeks.length === 1) {
         return selectedWeeks[0].label
      }
      return `${selectedWeeks.length} weeks selected`
   }

   return (
      <div className="period-selector-container" ref={wrapperRef}>
         <div
            className={`period-input-wrapper ${
               disabled ? "period-input-disabled" : "period-input-enabled"
            }`}
         >
            <input
               type="text"
               readOnly
               placeholder={isLoading ? "Loading" : placeholder}
               value={isLoading ? "Loading" : formatDisplayValue()}
               className="period-input-field"
               onClick={handleInputClick}
               disabled={disabled}
            />
            <div className="period-icon-container">
               {isLoading ? (
                  <Spinner className="period-loading-spinner" color="primary" />
               ) : (
                  <img
                     src={calendarIcon}
                     alt="Calendar Icon"
                     className="period-calendar-icon"
                     onClick={handleIconClick}
                  />
               )}
            </div>
         </div>

         {isOpen && (
            <div className="period-dropdown-menu">
               <div className="period-options-list">
                  {options.map((option, index) => {
                     const isSelected = isWeekSelected(option)
                     const canSelect = canSelectWeek(option)

                     return (
                        <div
                           key={index}
                           className={`period-option-item ${
                              isSelected ? "period-option-selected" : ""
                           } ${!canSelect ? "period-option-disabled" : ""}`}
                           onClick={() => canSelect && handleWeekSelect(option)}
                        >
                           <span className="period-option-label">
                              {option.label}
                           </span>
                           {isSelected && (
                              <span className="period-option-checkmark">✓</span>
                           )}
                        </div>
                     )
                  })}
               </div>
            </div>
         )}
      </div>
   )
}

export default WeekSelector
